// components/Reports/ReportsPage.jsx — Transaction Register
import React, { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { formatDate, formatCurrency, safeNum } from "../../utils/helpers";

// ─── Date filter helpers ──────────────────────────────────────────────────────
const today = new Date();
function subDays(n) { const d = new Date(today); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; }
const PRESETS = [
  { key: "all", label: "All Time" },
  { key: "4w",  label: "4 Weeks",  from: subDays(28) },
  { key: "1m",  label: "1 Month",  from: subDays(30) },
  { key: "3m",  label: "3 Months", from: subDays(90) },
  { key: "6m",  label: "6 Months", from: subDays(180) },
  { key: "1y",  label: "1 Year",   from: subDays(365) },
];

function inRange(dateStr, from, to) {
  if (!dateStr) return true;
  const d = dateStr.substring(0, 10);
  if (from && d < from) return false;
  if (to   && d > to)   return false;
  return true;
}

// Groups rows by their week/date field, returns sorted array of { week, rows }
function groupByWeek(rows, dateField) {
  const map = {};
  rows.forEach(row => {
    const week = (row[dateField] || row.weekEnding || "").substring(0, 10);
    if (!week) return;
    if (!map[week]) map[week] = [];
    map[week].push(row);
  });
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, wr]) => ({ week, rows: wr }));
}

function groupByMonth(rows, dateField) {
  const map = {};
  rows.forEach(row => {
    const d = (row[dateField] || row.weekEnding || "").substring(0, 7); // "YYYY-MM"
    if (!d || d.length < 7) return;
    if (!map[d]) map[d] = [];
    map[d].push(row);
  });
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, rows]) => {
      const [y, m] = month.split("-");
      const label = new Date(Number(y), Number(m) - 1, 1)
        .toLocaleDateString("en-GB", { month: "short", year: "numeric" });
      return { week: label, rows };
    });
}

// ─── Status badge colours ─────────────────────────────────────────────────────
const BADGE = {
  approved:            { bg: "#DCFCE7", color: "#15803D" },
  "pending-execution": { bg: "#FEF9C3", color: "#A16207" },
  pending:             { bg: "#FEF3C7", color: "#D97706" },
  open:                { bg: "#EFF6FF", color: "#2563EB" },
  receipted:           { bg: "#F0FDF4", color: "#15803D" },
  active:              { bg: "#F0F9FF", color: "#0284C7" },
  onboarded:           { bg: "#ECFDF5", color: "#059669" },
  closed:              { bg: "#F3F4F6", color: "#6B7280" },
  issued:              { bg: "#F5F3FF", color: "#7C3AED" },
  executed:            { bg: "#EFF6FF", color: "#1D4ED8" },
  awarded:             { bg: "#FFF7ED", color: "#C2410C" },
  Major:               { bg: "#FEF2F2", color: "#DC2626" },
  Minor:               { bg: "#EFF6FF", color: "#2563EB" },
  RFP:                 { bg: "#F5F3FF", color: "#7C3AED" },
  RFQ:                 { bg: "#EFF6FF", color: "#1D4ED8" },
  EOI:                 { bg: "#FFF7ED", color: "#C2410C" },
  meeting:             { bg: "#EFF6FF", color: "#1D4ED8" },
  community:           { bg: "#ECFDF5", color: "#065F46" },
  regulatory:          { bg: "#FDF4FF", color: "#7E22CE" },
  presentation:        { bg: "#EFF6FF", color: "#1D4ED8" },
  workshop:            { bg: "#F0FDF4", color: "#15803D" },
  briefing:            { bg: "#FFFBEB", color: "#92400E" },
  review:              { bg: "#F5F3FF", color: "#6D28D9" },
  negotiation:         { bg: "#FFF7ED", color: "#C2410C" },
  onboarding:          { bg: "#ECFDF5", color: "#065F46" },
  monthly:             { bg: "#EFF6FF", color: "#2563EB" },
  quarterly:           { bg: "#F0FDF4", color: "#15803D" },
  cancelled:           { bg: "#FEF2F2", color: "#DC2626" },
  "on hold":           { bg: "#FEF9C3", color: "#A16207" },
  revised:             { bg: "#EFF6FF", color: "#1D4ED8" },
  reviewed:            { bg: "#F0FDF4", color: "#15803D" },
  ncr:                 { bg: "#FFF7ED", color: "#C2410C" },
  cpr:                 { bg: "#F5F3FF", color: "#6D28D9" },
  "cost avoidance":    { bg: "#ECFDF5", color: "#059669" },
};

function BadgeCell({ value }) {
  const s = BADGE[value] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {value}
    </span>
  );
}

function formatCell(value, type) {
  if (value == null || value === "") return <span style={{ color: "#D1D5DB" }}>—</span>;
  if (type === "currency")     return <span style={{ fontWeight: 700, color: "#0052CC" }}>{formatCurrency(value)}</span>;
  if (type === "currency_ngn") return <span style={{ fontWeight: 700, color: "#0052CC" }}>{formatCurrency(value, "₦")}</span>;
  if (type === "currency$")    return value > 0 ? <span style={{ fontWeight: 700, color: "#059669" }}>${Number(value).toLocaleString()}</span> : <span style={{ color: "#D1D5DB" }}>—</span>;
  if (type === "number")     return <span style={{ fontWeight: 600, color: "#111827" }}>{Number(value).toLocaleString()}</span>;
  if (type === "date")       return <span style={{ fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>{formatDate(value)}</span>;
  if (type === "badge")      return <BadgeCell value={value} />;
  if (type === "percentage") return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 80 }}>
      <div style={{ width: 44, height: 5, background: "#F3F4F6", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, safeNum(value) * 100)}%`, height: "100%", background: "linear-gradient(90deg,#0052CC,#0041B8)", borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "#0052CC" }}>{Math.round(safeNum(value) * 100)}%</span>
    </div>
  );
  if (type === "score") {
    const n = safeNum(value);
    const color = n >= 85 ? "#15803D" : n >= 70 ? "#D97706" : "#DC2626";
    return <span style={{ fontWeight: 700, color, background: color + "18", padding: "2px 8px", borderRadius: 6, fontSize: 12 }}>{n}</span>;
  }
  // text — uppercase, truncate long strings with tooltip
  const s = String(value);
  return <span title={s.length > 60 ? s : undefined} style={{ fontSize: 12, color: "#374151", maxWidth: 260, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase" }}>{s}</span>;
}

// ─── Tab configuration ────────────────────────────────────────────────────────
const TABS = [
  {
    key: "contracts", label: "Contracts", icon: "📋",
    collectionKey: "contracts", dateField: "date",
    columns: [
      { key: "date",     label: "Date",           type: "date",     w: 100 },
      { key: "entity",   label: "Entity",         type: "text",     w: 160 },
      { key: "title",    label: "Contract Title", type: "text",     w: 260 },
      { key: "vendor",   label: "Supplier",       type: "text",     w: 180 },
      { key: "value",    label: "Value",          type: "currency", w: 130 },
      { key: "type",     label: "Status",         type: "badge",    w: 110 },
      { key: "notes",    label: "Notes",          type: "text",     w: 200 },
    ],
    summary: (rows) => [
      { label: "Total Contracts",  value: rows.length,                                             fmt: "n",  accent: "#0052CC" },
      { label: "Executed",         value: rows.filter(r => r.type === "executed").length,           fmt: "n",  accent: "#107C10" },
      { label: "Awarded",          value: rows.filter(r => r.type === "awarded").length,            fmt: "n",  accent: "#986F0B" },
      { label: "Total Value",      value: rows.reduce((s, r) => s + safeNum(r.value), 0),          fmt: "$",  accent: "#5C2D91" },
    ],
    editFields: [
      { key: "weekEnding", label: "Week Ending",   inputType: "date" },
      { key: "date",       label: "Date",           inputType: "date" },
      { key: "entity",     label: "Entity",         inputType: "text" },
      { key: "title",      label: "Contract Title", inputType: "text" },
      { key: "vendor",     label: "Supplier",       inputType: "text" },
      { key: "value",      label: "Value ($)",      inputType: "number" },
      { key: "type",       label: "Status",         inputType: "select", options: ["awarded","executed","cancelled","closed"] },
      { key: "notes",      label: "Notes",          inputType: "textarea" },
    ],
  },
  {
    key: "purchaseOrders", label: "Purchase Orders", icon: "🛒",
    collectionKey: "purchaseOrders", dateField: "date",
    columns: [
      { key: "date",        label: "Date",        type: "date",     w: 100 },
      { key: "entity",      label: "Entity",      type: "text",     w: 150 },
      { key: "poNumber",    label: "PO No.",       type: "text",     w: 130 },
      { key: "vendor",      label: "Supplier",    type: "text",     w: 180 },
      { key: "description", label: "Description", type: "text",     w: 200 },
      { key: "amountUSD",   label: "Amount ($)",  type: "currency", w: 130 },
      { key: "category",    label: "Category",    type: "text",     w: 140 },
      { key: "subcategory", label: "Subcategory", type: "text",     w: 140 },
    ],
    summary: (rows, allTx, from, to) => {
      const totalSpend = rows.reduce((s, r) => s + safeNum(r.amountUSD), 0);
      return [
        { label: "Total POs",   value: rows.length,   fmt: "n",    accent: "#0052CC" },
        { label: "Total Spend", value: totalSpend,     fmt: "$",    accent: "#5C2D91" },
      ];
    },
    editFields: [
      { key: "weekEnding",  label: "Week Ending",  inputType: "date" },
      { key: "date",        label: "Date",          inputType: "date" },
      { key: "entity",      label: "Entity",        inputType: "text" },
      { key: "poNumber",    label: "PO Number",     inputType: "text" },
      { key: "vendor",      label: "Supplier",      inputType: "text" },
      { key: "description", label: "Description",   inputType: "text" },
      { key: "amountUSD",   label: "Amount ($)",    inputType: "number" },
      { key: "category",    label: "Category",      inputType: "text" },
      { key: "subcategory", label: "Subcategory",   inputType: "text" },
    ],
  },
  {
    key: "cscMemos", label: "CSC Memos", icon: "📝",
    collectionKey: "cscMemos", dateField: "date",
    columns: [
      { key: "date",     label: "Date",       type: "date",     w: 100 },
      { key: "entity",   label: "Entity",     type: "text",     w: 160 },
      { key: "title",    label: "Memo Title", type: "text",     w: 280 },
      { key: "value",    label: "Value",      type: "currency", w: 130 },
      { key: "status",   label: "Status",     type: "badge",    w: 110 },
      { key: "comments", label: "Comments",   type: "text",     w: 220 },
    ],
    summary: (rows) => [
      { label: "Total Memos",  value: rows.length,                                        fmt: "n", accent: "#0052CC" },
      { label: "Total Value",  value: rows.reduce((s, r) => s + safeNum(r.value), 0),    fmt: "$", accent: "#5C2D91" },
    ],
    editFields: [
      { key: "weekEnding", label: "Week Ending", inputType: "date" },
      { key: "date",       label: "Date",         inputType: "date" },
      { key: "entity",     label: "Entity",       inputType: "text" },
      { key: "title",      label: "Memo Title",   inputType: "text" },
      { key: "value",      label: "Value ($)",    inputType: "number" },
      { key: "status",     label: "Status",       inputType: "select", options: ["approved","revised","cancelled","reviewed","on hold"] },
      { key: "comments",   label: "Comments",     inputType: "textarea" },
    ],
  },
  {
    key: "tenderBoardItems", label: "Tender Board", icon: "⚖️",
    collectionKey: "tenderBoardItems", dateField: "date",
    columns: [
      { key: "date",     label: "Date",         type: "date",     w: 100 },
      { key: "entity",   label: "Entity",       type: "text",     w: 150 },
      { key: "tbNumber", label: "TB No.",        type: "text",     w: 130 },
      { key: "title",    label: "Proposal",     type: "text",     w: 240 },
      { key: "supplier", label: "Supplier",     type: "text",     w: 160 },
      { key: "amount",   label: "Amount",       type: "currency", w: 140 },
      { key: "type",     label: "TB Type",      type: "badge",    w: 100 },
      { key: "status",   label: "Status",       type: "badge",    w: 110 },
    ],
    summary: (rows) => [
      { label: "Total Items",  value: rows.length,                                                         fmt: "n", accent: "#0052CC" },
      { label: "Major TB",     value: rows.filter(r => r.type === "Major").length,                         fmt: "n", accent: "#DC2626" },
      { label: "Minor TB",     value: rows.filter(r => r.type === "Minor").length,                         fmt: "n", accent: "#2563EB" },
      { label: "Major Value",  value: rows.filter(r => r.type === "Major").reduce((s,r)=>s+safeNum(r.amount),0), fmt: "$", accent: "#DC2626" },
      { label: "Minor Value",  value: rows.filter(r => r.type === "Minor").reduce((s,r)=>s+safeNum(r.amount),0), fmt: "$", accent: "#2563EB" },
    ],
    editFields: [
      { key: "weekEnding", label: "Week Ending",    inputType: "date" },
      { key: "date",       label: "Date",            inputType: "date" },
      { key: "entity",     label: "Entity",          inputType: "text" },
      { key: "tbNumber",   label: "TB Number",       inputType: "text" },
      { key: "title",      label: "Proposal Title",  inputType: "text" },
      { key: "supplier",   label: "Supplier",        inputType: "text" },
      { key: "amount",     label: "Amount ($)",      inputType: "number" },
      { key: "type",       label: "TB Type",         inputType: "select", options: ["Major","Minor"] },
      { key: "status",     label: "Status",          inputType: "select", options: ["approved","pending"] },
    ],
  },
  {
    key: "rfxItems", label: "RFX", icon: "📨",
    collectionKey: "rfxItems", dateField: "date",
    columns: [
      { key: "date",   label: "Date",      type: "date",  w: 100 },
      { key: "entity", label: "Entity",    type: "text",  w: 160 },
      { key: "title",  label: "RFX Title", type: "text",  w: 280 },
      { key: "type",   label: "Type",      type: "badge", w: 90  },
      { key: "status", label: "Status",    type: "badge", w: 100 },
    ],
    summary: (rows) => [
      { label: "Total Issued", value: rows.length,                                         fmt: "n", accent: "#0052CC" },
      { label: "RFP",          value: rows.filter(r => r.type === "RFP").length,           fmt: "n", accent: "#7C3AED" },
      { label: "RFQ",          value: rows.filter(r => r.type === "RFQ").length,           fmt: "n", accent: "#1D4ED8" },
      { label: "EOI",          value: rows.filter(r => r.type === "EOI").length,           fmt: "n", accent: "#C2410C" },
      { label: "Entities",     value: new Set(rows.map(r => r.entity).filter(Boolean)).size, fmt: "n", accent: "#008272" },
    ],
    editFields: [
      { key: "weekEnding", label: "Week Ending", inputType: "date" },
      { key: "date",       label: "Date",         inputType: "date" },
      { key: "entity",     label: "Entity",       inputType: "text" },
      { key: "title",      label: "RFX Title",    inputType: "text" },
      { key: "type",       label: "Type",         inputType: "select", options: ["RFP","RFQ","EOI"] },
      { key: "status",     label: "Status",       inputType: "select", options: ["issued","closed","cancelled"] },
    ],
  },
  {
    key: "suppliers", label: "Suppliers", icon: "🏢",
    collectionKey: "suppliers", dateField: "date",
    columns: [
      { key: "date",          label: "Date",        type: "date",  w: 100 },
      { key: "name",          label: "Supplier",    type: "text",  w: 220 },
      { key: "category",      label: "Category",   type: "text",  w: 160 },
      { key: "status",        label: "Status",     type: "badge", w: 110 },
      { key: "contactPerson", label: "Contact",    type: "text",  w: 160 },
      { key: "email",         label: "Email",      type: "text",  w: 200 },
    ],
    summary: (rows) => [
      { label: "Total Onboarded", value: rows.length,                                         fmt: "n", accent: "#0052CC" },
      { label: "Categories",      value: new Set(rows.map(r => r.category).filter(Boolean)).size, fmt: "n", accent: "#008272" },
    ],
    editFields: [
      { key: "weekEnding",    label: "Week Ending",    inputType: "date" },
      { key: "date",          label: "Date",            inputType: "date" },
      { key: "name",          label: "Supplier Name",  inputType: "text" },
      { key: "category",      label: "Category",       inputType: "text" },
      { key: "status",        label: "Status",         inputType: "select", options: ["onboarded","suspended","inactive"] },
      { key: "contactPerson", label: "Contact Person", inputType: "text" },
      { key: "email",         label: "Email",          inputType: "text" },
    ],
  },
  {
    key: "stakeholderEngagements", label: "Stakeholder Engagements", icon: "🤝",
    collectionKey: "stakeholderEngagements", dateField: "date",
    columns: [
      { key: "date",        label: "Date",        type: "date",  w: 100 },
      { key: "description", label: "Description", type: "text",  w: 300 },
      { key: "stakeholder", label: "Stakeholder", type: "text",  w: 200 },
      { key: "type",        label: "Type",        type: "badge", w: 140 },
    ],
    summary: (rows) => [
      { label: "Total Engagements", value: rows.length,                                            fmt: "n", accent: "#0052CC" },
      { label: "Meetings",          value: rows.filter(r => r.type === "meeting").length,          fmt: "n", accent: "#1D4ED8" },
      { label: "Regulatory",        value: rows.filter(r => r.type === "regulatory").length,       fmt: "n", accent: "#7E22CE" },
      { label: "NCR",               value: rows.filter(r => r.type === "ncr").length,              fmt: "n", accent: "#C2410C" },
      { label: "CPR",               value: rows.filter(r => r.type === "cpr").length,              fmt: "n", accent: "#6D28D9" },
    ],
    editFields: [
      { key: "weekEnding",  label: "Week Ending",  inputType: "date" },
      { key: "date",        label: "Date",          inputType: "date" },
      { key: "description", label: "Description",  inputType: "textarea" },
      { key: "stakeholder", label: "Stakeholder",  inputType: "text" },
      { key: "type",        label: "Type",         inputType: "select", options: ["meeting","ncr","negotiation","regulatory","presentation","workshop","cpr"] },
    ],
  },
  {
    key: "costSavings", label: "Cost Savings", icon: "💰",
    collectionKey: "costSavings", dateField: "date",
    columns: [
      { key: "date",             label: "Date",               type: "date",       w: 100 },
      { key: "entity",           label: "Entity",             type: "text",       w: 140 },
      { key: "category",         label: "Category",           type: "text",       w: 160 },
      { key: "description",      label: "Description",        type: "text",       w: 220 },
      { key: "supplier",         label: "Supplier",           type: "text",       w: 180 },
      { key: "baselineCost",     label: "Baseline Cost ($)",  type: "currency",   w: 140 },
      { key: "negotiatedCost",   label: "Negotiated Cost ($)",type: "currency",   w: 150 },
      { key: "savingsAmount",    label: "Savings ($)",        type: "currency",   w: 130 },
      { key: "savingsPercent",   label: "Savings %",          type: "percentage", w: 90  },
      { key: "savingsType",      label: "Savings Type",       type: "badge",      w: 180 },
      { key: "notes",            label: "Notes",              type: "text",       w: 200 },
    ],
    summary: (rows) => [
      { label: "Total Records",  value: rows.length,                                                fmt: "n", accent: "#0052CC" },
      { label: "Total Savings",  value: rows.reduce((s, r) => s + safeNum(r.savingsAmount), 0),    fmt: "$", accent: "#107C10" },
      { label: "Total Baseline", value: rows.reduce((s, r) => s + safeNum(r.baselineCost), 0),     fmt: "$", accent: "#605E5C" },
    ],
    editFields: [
      { key: "weekEnding",       label: "Week Ending",          inputType: "date" },
      { key: "date",             label: "Date",                  inputType: "date" },
      { key: "entity",           label: "Entity",                inputType: "text" },
      { key: "category",         label: "Category",              inputType: "text" },
      { key: "description",      label: "Description",           inputType: "textarea" },
      { key: "supplier",         label: "Supplier",              inputType: "text" },
      { key: "baselineCost",     label: "Baseline Cost ($)",     inputType: "number" },
      { key: "negotiatedCost",   label: "Negotiated Cost ($)",   inputType: "number" },
      { key: "savingsAmount",    label: "Savings Amount ($)",    inputType: "number" },
      { key: "savingsPercent",   label: "Savings % (0–1)",       inputType: "number" },
      { key: "savingsType",      label: "Savings Type",          inputType: "select", options: ["Negotiation","Re-scope","Alternative Supplier","Demand Reduction","Specification Change","Contract Consolidation","Volume Discount","Cost Avoidance"] },
      { key: "notes",            label: "Notes",                 inputType: "textarea" },
    ],
  },
  {
    key: "dieselRecords", label: "Diesel", icon: "⛽",
    collectionKey: "dieselRecords", dateField: "weekEnding",
    columns: [
      { key: "weekEnding",   label: "Week Ending",    type: "date",   w: 120 },
      { key: "openingStock", label: "Opening (L)",   type: "number", w: 120 },
      { key: "supply",       label: "Supply (L)",    type: "number", w: 110 },
      { key: "consumption",  label: "Consumption (L)",type:"number", w: 130 },
      { key: "closingStock", label: "Closing (L)",   type: "number", w: 120 },
      { key: "location",     label: "Location",      type: "text",   w: 130 },
    ],
    summary: (rows) => [
      { label: "Weeks",            value: rows.length,                                                fmt: "n",  accent: "#0052CC" },
      { label: "Total Supply",     value: rows.reduce((s,r) => s + safeNum(r.supply), 0),            fmt: "L",  accent: "#107C10" },
      { label: "Total Consumed",   value: rows.reduce((s,r) => s + safeNum(r.consumption), 0),       fmt: "L",  accent: "#D83B01" },
      { label: "Latest Closing",   value: rows.length ? rows[0].closingStock : 0,                    fmt: "L",  accent: "#008272" },
    ],
    editFields: [
      { key: "weekEnding",   label: "Week Ending",    inputType: "date" },
      { key: "openingStock", label: "Opening Stock (L)", inputType: "number" },
      { key: "supply",       label: "Supply (L)",      inputType: "number" },
      { key: "consumption",  label: "Consumption (L)", inputType: "number" },
      { key: "closingStock", label: "Closing Stock (L)",inputType: "number" },
      { key: "location",     label: "Location",        inputType: "text" },
    ],
  },
  {
    key: "logisticsRecords", label: "Logistics", icon: "🚚",
    collectionKey: "logisticsRecords", dateField: "weekEnding",
    columns: [
      { key: "weekEnding", label: "Week Ending", type: "date",     w: 120 },
      { key: "route",      label: "Route",       type: "text",     w: 150 },
      { key: "trips",      label: "Trips",       type: "number",   w: 90  },
      { key: "spend",      label: "Spend",       type: "currency_ngn", w: 130 },
      { key: "notes",      label: "Notes",       type: "text",     w: 220 },
    ],
    summary: (rows) => [
      { label: "Weeks",        value: rows.length,                                          fmt: "n", accent: "#0052CC" },
      { label: "Total Trips",  value: rows.reduce((s,r) => s + safeNum(r.trips), 0),       fmt: "n", accent: "#107C10" },
      { label: "Total Spend",  value: rows.reduce((s,r) => s + safeNum(r.spend), 0),       fmt: "₦", accent: "#5C2D91" },
      { label: "Avg Trips/Wk",value: rows.length ? (rows.reduce((s,r)=>s+safeNum(r.trips),0)/rows.length).toFixed(1) : 0, fmt: "raw", accent: "#008272" },
    ],
    editFields: [
      { key: "weekEnding", label: "Week Ending",  inputType: "date" },
      { key: "route",      label: "Route",        inputType: "text" },
      { key: "trips",      label: "Trips",        inputType: "number" },
      { key: "spend",      label: "Spend (₦)",    inputType: "number" },
      { key: "notes",      label: "Notes",        inputType: "textarea" },
    ],
  },
  {
    key: "projectUpdates", label: "Projects", icon: "🏗️",
    collectionKey: "projectUpdates", dateField: "weekEnding",
    columns: [
      { key: "weekEnding",  label: "Week Ending", type: "date",       w: 120 },
      { key: "projectName", label: "Project",     type: "text",       w: 220 },
      { key: "progress",    label: "Progress",    type: "percentage", w: 140 },
      { key: "notes",       label: "Notes",       type: "text",       w: 260 },
    ],
    summary: (rows) => {
      const projects = [...new Set(rows.map(r => r.projectName).filter(Boolean))];
      return [
        { label: "Total Updates", value: rows.length,       fmt: "n",    accent: "#0052CC" },
        { label: "Projects",      value: projects.length,   fmt: "n",    accent: "#008272" },
        { label: "Latest Update", value: rows.length ? formatDate(rows[0]?.weekEnding) : "—", fmt: "text", accent: "#5C2D91" },
      ];
    },
    editFields: [
      { key: "weekEnding",  label: "Week Ending",  inputType: "date" },
      { key: "key",         label: "Project Key",  inputType: "select", options: ["uyoPortacabins","uyoBaseProgress","cateringProgress"] },
      { key: "projectName", label: "Project Name", inputType: "text" },
      { key: "progress",    label: "Progress (0–1)",inputType: "number" },
      { key: "notes",       label: "Notes",        inputType: "textarea" },
    ],
  },
  {
    key: "processNotes", label: "Notes & Highlights", icon: "📌",
    collectionKey: "processNotes", dateField: "weekEnding",
    columns: [
      { key: "weekEnding",  label: "Week Ending",        type: "date", w: 120 },
      { key: "highlights",  label: "Weekly Highlights",  type: "text", w: 400 },
      { key: "text",        label: "Process Improvement", type: "text", w: 400 },
    ],
    summary: () => [], // no summary cards for Notes & Highlights
    editFields: [
      { key: "weekEnding",  label: "Week Ending",         inputType: "date" },
      { key: "highlights",  label: "Weekly Highlights",   inputType: "textarea" },
      { key: "text",        label: "Process Improvement", inputType: "textarea" },
    ],
  },
];

// ─── Per-tab chart configurations ────────────────────────────────────────────
// Each entry: { title, type:"bar"|"line", getData(rows,dateField)->{data,series} }
const DYN_COLORS = ["#0052CC","#107C10","#D83B01","#5C2D91","#008272","#986F0B"];

const TAB_CHARTS = {
  contracts: { charts: [
    { title:"Contracts by Month", type:"bar",
      getData:(rows,df)=>({ series:[{key:"executed",name:"Executed",color:"#107C10"},{key:"awarded",name:"Awarded",color:"#D97706"},{key:"cancelled",name:"Cancelled",color:"#DC2626"},{key:"closed",name:"Closed",color:"#6B7280"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,executed:w.filter(r=>r.type==="executed").length,awarded:w.filter(r=>r.type==="awarded").length,cancelled:w.filter(r=>r.type==="cancelled").length,closed:w.filter(r=>r.type==="closed").length})) })},
    { title:"Contract Value by Month ($M)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"value",name:"Value ($M)",color:"#5C2D91"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,value:+(w.reduce((s,r)=>s+safeNum(r.value),0)/1e6).toFixed(2)})) })},
  ]},
  purchaseOrders: { charts: [
    { title:"PO Spend by Month ($M)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"spend",name:"Spend ($M)",color:"#0052CC"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,spend:+(w.reduce((s,r)=>s+safeNum(r.amountUSD),0)/1e6).toFixed(2)})) })},
    { title:"PO Count by Month", type:"bar",
      getData:(rows,df)=>({ series:[{key:"count",name:"POs",color:"#008272"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,count:w.length})) })},
  ]},
  cscMemos: { charts: [
    { title:"Memo Value by Month ($M)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"value",name:"Value ($M)",color:"#5C2D91"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,value:+(w.reduce((s,r)=>s+safeNum(r.value),0)/1e6).toFixed(2)})) })},
  ]},
  tenderBoardItems: { charts: [
    { title:"Tender Board Amounts by Month ($M)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"major",name:"Major ($M)",color:"#DC2626"},{key:"minor",name:"Minor ($M)",color:"#2563EB"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,major:+(w.filter(r=>r.type==="Major").reduce((s,r)=>s+safeNum(r.amount),0)/1e6).toFixed(2),minor:+(w.filter(r=>r.type==="Minor").reduce((s,r)=>s+safeNum(r.amount),0)/1e6).toFixed(2)})) })},
  ]},
  rfxItems: { charts: [
    { title:"RFX Issued by Month", type:"bar",
      getData:(rows,df)=>({ series:[{key:"RFP",name:"RFP",color:"#7C3AED"},{key:"RFQ",name:"RFQ",color:"#1D4ED8"},{key:"EOI",name:"EOI",color:"#C2410C"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,RFP:w.filter(r=>r.type==="RFP").length,RFQ:w.filter(r=>r.type==="RFQ").length,EOI:w.filter(r=>r.type==="EOI").length})) })},
  ]},
  suppliers: { charts: [
    { title:"Suppliers Onboarded by Month", type:"bar",
      getData:(rows,df)=>({ series:[{key:"count",name:"Onboarded",color:"#008272"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,count:w.length})) })},
  ]},
  stakeholderEngagements: { charts: [] },
  dieselRecords: { charts: [
    { title:"Diesel Closing Stock by Month (L)", type:"line",
      getData:(rows,df)=>{const locs=[...new Set(rows.map(r=>r.location).filter(Boolean))];return {series:locs.map((l,i)=>({key:l,name:l,color:DYN_COLORS[i%DYN_COLORS.length]})),data:groupByMonth(rows,df).map(({week,rows:w})=>{const e={week};locs.forEach(l=>{const r=w.find(x=>x.location===l);e[l]=r?safeNum(r.closingStock):null;});return e;})};},
    },
    { title:"Diesel Supply & Consumption by Month (L)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"supply",name:"Supply",color:"#107C10"},{key:"consumption",name:"Consumption",color:"#D83B01"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,supply:w.reduce((s,r)=>s+safeNum(r.supply),0),consumption:w.reduce((s,r)=>s+safeNum(r.consumption),0)})) })},
  ]},
  logisticsRecords: { charts: [
    { title:"Trips by Month", type:"bar",
      getData:(rows,df)=>({ series:[{key:"trips",name:"Trips",color:"#008272"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,trips:w.reduce((s,r)=>s+safeNum(r.trips),0)})) })},
    { title:"Logistics Spend by Month (₦M)", type:"line",
      getData:(rows,df)=>({ series:[{key:"spend",name:"Spend (₦M)",color:"#5C2D91"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,spend:+(w.reduce((s,r)=>s+safeNum(r.spend),0)/1e6).toFixed(2)})) })},
  ]},
  projectUpdates: { charts: [
    { title:"Project Progress by Month (%)", type:"line",
      getData:(rows,df)=>{const projs=[...new Set(rows.map(r=>r.projectName).filter(Boolean))];return {series:projs.map((p,i)=>({key:p,name:p,color:DYN_COLORS[i%DYN_COLORS.length]})),data:groupByMonth(rows,df).map(({week,rows:w})=>{const e={week};projs.forEach(p=>{const r=w.find(x=>x.projectName===p);e[p]=r?+(safeNum(r.progress)*100).toFixed(0):null;});return e;})};},
    },
  ]},
  costSavings: { charts: [
    { title:"Savings Amount by Month ($M)", type:"bar",
      getData:(rows,df)=>({ series:[{key:"savings",name:"Savings ($M)",color:"#107C10"}],
        data:groupByMonth(rows,df).map(({week,rows:w})=>({week,savings:+(w.reduce((s,r)=>s+safeNum(r.savingsAmount),0)/1e6).toFixed(2)})) })},
    { title:"Savings by Type by Month ($M)", type:"bar",
      getData:(rows,df)=>{const types=[...new Set(rows.map(r=>r.savingsType).filter(Boolean))].slice(0,6);return {series:types.map((t,i)=>({key:t,name:t,color:DYN_COLORS[i%DYN_COLORS.length]})),data:groupByMonth(rows,df).map(({week,rows:w})=>{const e={week};types.forEach(t=>{e[t]=+(w.filter(r=>r.savingsType===t).reduce((s,r)=>s+safeNum(r.savingsAmount),0)/1e6).toFixed(2);});return e;})};},
    },
  ]},
  processNotes: { charts: [] },
};

// ─── In-app Recharts tab charts ───────────────────────────────────────────────
const axStyle = { fontSize: 10, fill: "#8A8886" };

function ChartBody({ type, data, series, height = 200 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === "line" ? (
        <LineChart data={data} margin={{top:5,right:10,left:0,bottom:55}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis dataKey="week" tick={{...axStyle,angle:-35,textAnchor:"end"}} interval={0} />
          <YAxis tick={axStyle} />
          <Tooltip />
          <Legend verticalAlign="bottom" wrapperStyle={{fontSize:11,paddingTop:8}} />
          {series.map(s=><Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2} dot={{r:2.5}} connectNulls />)}
        </LineChart>
      ) : (
        <BarChart data={data} margin={{top:5,right:10,left:0,bottom:55}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
          <XAxis dataKey="week" tick={{...axStyle,angle:-35,textAnchor:"end"}} interval={0} />
          <YAxis tick={axStyle} />
          <Tooltip />
          <Legend verticalAlign="bottom" wrapperStyle={{fontSize:11,paddingTop:8}} />
          {series.map(s=><Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4,4,0,0]} />)}
        </BarChart>
      )}
    </ResponsiveContainer>
  );
}

function TabCharts({ tabKey, rows, dateField, onFullscreen }) {
  const cfgs = TAB_CHARTS[tabKey]?.charts || [];
  if (!cfgs.length || rows.length < 2) return null;
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(420px,1fr))", gap:16, marginBottom:20 }}>
      {cfgs.map((cfg, i) => {
        const { data, series } = cfg.getData(rows, dateField);
        if (!data?.length) return null;
        return (
          <div key={i} style={{ background:"#fff", borderRadius:14, padding:"18px 20px", border:"1px solid #EAECEF", boxShadow:"0 2px 10px rgba(0,0,0,0.05)", position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>{cfg.title}</div>
              <button
                onClick={() => onFullscreen?.({ title: cfg.title, type: cfg.type, data, series })}
                style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"#EBF3FF", border:"1.5px solid #C7E0FF", borderRadius:8, cursor:"pointer", fontSize:11, fontWeight:700, color:"#0052CC", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}
                onMouseEnter={e=>{e.currentTarget.style.background="#0052CC";e.currentTarget.style.color="#fff";e.currentTarget.style.borderColor="#0052CC";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#EBF3FF";e.currentTarget.style.color="#0052CC";e.currentTarget.style.borderColor="#C7E0FF";}}
              >⛶ Full Screen</button>
            </div>
            <ChartBody type={cfg.type} data={data} series={series} height={200} />
          </div>
        );
      })}
    </div>
  );
}

// ─── SVG chart for HTML export (self-contained, no dependencies) ─────────────
function buildSVGChart(cfg, data, series, W=520, H=200) {
  if (!data.length) return "";
  const P = {t:12,r:12,b:72,l:48};
  const iW = W-P.l-P.r, iH = H-P.t-P.b;
  const vals = data.flatMap(d=>series.map(s=>d[s.key]!=null?Number(d[s.key]):0));
  const maxV = Math.max(...vals,1);
  const f = (n,d=1)=>Number(n).toFixed(d);
  const fmt = v=>v>=1e6?f(v/1e6)+'M':v>=1e3?f(v/1e3,0)+'k':f(v,0);

  const legend = series.slice(0,6).map((s,i)=>{
    const lx=4+i*Math.min(100,(W-8)/series.length);
    const ly = P.t+iH+52;
    return `<rect x="${f(lx)}" y="${f(ly)}" width="10" height="10" fill="${s.color}" rx="2"/>
            <text x="${f(lx+13)}" y="${f(ly+9.5)}" font-size="9.5" fill="#374151">${s.name}</text>`;
  }).join("");

  let grid="";
  for(let t=0;t<=4;t++){
    const pct=t/4, y=P.t+iH-pct*iH;
    grid+=`<line x1="${P.l}" y1="${f(y)}" x2="${W-P.r}" y2="${f(y)}" stroke="${t===0?"#E5E7EB":"#F3F4F6"}"/>`;
    if(t>0)grid+=`<text x="${f(P.l-3)}" y="${f(y+3.5)}" text-anchor="end" font-size="9" fill="#9CA3AF">${fmt(maxV*pct)}</text>`;
  }

  const step=Math.max(1,Math.ceil(data.length/12));
  const isLine=cfg.type==="line";

  const xLabel=(d,i,cx)=>{
    if(i%step!==0)return "";
    const lbl=(d.week||"").substring(5);
    return `<text x="${f(cx)}" y="${f(P.t+iH+15)}" text-anchor="end" transform="rotate(-35 ${f(cx)} ${f(P.t+iH+15)})" font-size="8.5" fill="#9CA3AF">${lbl}</text>`;
  };

  let marks="";
  if(isLine){
    series.forEach(s=>{
      const pts=data.map((d,i)=>{
        if(d[s.key]==null)return null;
        const cx=P.l+(i/Math.max(data.length-1,1))*iW;
        const cy=P.t+iH-(Number(d[s.key])/maxV)*iH;
        return {cx,cy,i,d};
      }).filter(Boolean);
      if(!pts.length)return;
      marks+=`<polyline points="${pts.map(p=>`${f(p.cx)},${f(p.cy)}`).join(" ")}" fill="none" stroke="${s.color}" stroke-width="2.5" stroke-linejoin="round"/>`;
      marks+=pts.map(p=>`<circle cx="${f(p.cx)}" cy="${f(p.cy)}" r="3" fill="${s.color}"/>`).join("");
    });
    marks+=data.map((d,i)=>xLabel(d,i,P.l+(i/Math.max(data.length-1,1))*iW)).join("");
  } else {
    const gW=iW/data.length, bW=Math.max(3,(gW*0.8)/series.length);
    data.forEach((d,gi)=>{
      series.forEach((s,si)=>{
        const v=d[s.key]!=null?Number(d[s.key]):0;
        const bh=Math.max(0,(v/maxV)*iH);
        const bx=P.l+gi*gW+(gW-series.length*bW)/2+si*bW;
        const by=P.t+iH-bh;
        marks+=`<rect x="${f(bx)}" y="${f(by)}" width="${f(bW)}" height="${f(bh)}" fill="${s.color}" rx="2"/>`;
      });
      marks+=xLabel(d,gi,P.l+gi*gW+gW/2);
    });
  }

  return `<svg viewBox="0 0 ${W} ${H}" style="width:100%;display:block" xmlns="http://www.w3.org/2000/svg">
    <g font-family="Segoe UI,sans-serif"><g>${legend}</g>${grid}${marks}</g>
  </svg>`;
}

// ─── Summary stat card ────────────────────────────────────────────────────────
function SummaryCard({ label, value, fmt, accent }) {
  let display = value;
  if (fmt === "$")    display = formatCurrency(value);
  else if (fmt === "₦") display = formatCurrency(value, "₦");
  else if (fmt === "L")    display = `${Number(value).toLocaleString()} L`;
  else if (fmt === "days") display = `${value} d`;
  else if (fmt === "pct")  display = `${value}%`;
  else if (fmt === "n")    display = Number(value).toLocaleString();
  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "14px 18px", border: "1px solid #EAECEF", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", position: "relative", overflow: "hidden", minWidth: 120 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent, borderRadius: "12px 12px 0 0" }} />
      <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.2, marginTop: 4 }}>{display}</div>
      <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

// Natural key builders — mirrors useSCMData NATURAL_KEYS for duplicate detection
const DUP_KEY = {
  contracts:              r => `${r.weekEnding}|${String(r.title||"").trim().toLowerCase()}|${String(r.vendor||"").trim().toLowerCase()}`,
  purchaseOrders:         r => r.poNumber ? String(r.poNumber).trim() : `${r.weekEnding}|${String(r.vendor||"").trim().toLowerCase()}|${String(r.description||"").trim().toLowerCase()}`,
  cscMemos:               r => `${r.date||r.weekEnding}|${String(r.title||"").trim().toLowerCase()}`,
  tenderBoardItems:       r => r.tbNumber ? String(r.tbNumber).trim() : `${r.date||r.weekEnding}|${String(r.title||"").trim().toLowerCase()}`,
  suppliers:              r => String(r.name||"").trim().toLowerCase(),
  rfxItems:               r => `${r.weekEnding}|${String(r.title||"").trim().toLowerCase()}`,
  stakeholderEngagements: r => `${r.date||r.weekEnding}|${String(r.description||"").trim().toLowerCase()}`,
  dieselRecords:          r => `${r.weekEnding}|${String(r.location||"").trim().toLowerCase()}`,
  logisticsRecords:       r => `${r.weekEnding}|${String(r.route||"").trim().toLowerCase()}`,
  projectUpdates:         r => `${r.weekEnding}|${String(r.key||"").trim()}`,
  processNotes:           r => String(r.weekEnding||"").trim(),
  costSavings:            r => `${r.date||r.weekEnding}|${String(r.description||"").trim().toLowerCase()}|${String(r.supplier||"").trim().toLowerCase()}`,
};

// ─── Edit / Add Drawer ────────────────────────────────────────────────────────
function EditDrawer({ record, isNew, collectionKey, editFields, onClose, onSave, onAdd, existingRows = [] }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(false);

  useEffect(() => {
    if (record) { setForm(isNew ? {} : { ...record }); setDupWarning(false); }
  }, [record, isNew]);

  if (!record) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (force = false) => {
    if (isNew && !force) {
      const keyFn = DUP_KEY[collectionKey];
      if (keyFn) {
        const newKey = keyFn(form);
        const isDup = newKey && newKey !== "|" && existingRows.some(r => keyFn(r) === newKey);
        if (isDup) { setDupWarning(true); return; }
      }
    }
    setDupWarning(false);
    setSaving(true);
    if (isNew) {
      await onAdd?.(collectionKey, { ...form, id: `m-${Date.now()}` });
    } else {
      await onSave(collectionKey, record.id, form);
    }
    setSaving(false);
    onClose();
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 998, background: "rgba(0,15,40,0.45)", backdropFilter: "blur(3px)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999, width: "min(480px,100vw)", background: "#fff", boxShadow: "-8px 0 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F0F0F0", background: isNew ? "linear-gradient(135deg,#107C10,#0B5C0B)" : "linear-gradient(135deg,#001A3D,#003080)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{isNew ? "New Record" : "Edit Record"}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{isNew ? `Add ${collectionKey.replace(/([A-Z])/g," $1").trim()}` : "Update Transaction"}</div>
            </div>
            <button onClick={onClose} style={{ width: 34, height: 34, borderRadius: 9, border: "1.5px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", cursor: "pointer", fontSize: 18, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
        </div>
        {/* Fields */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {dupWarning && (
            <div style={{ background: "#FEF3C7", border: "1.5px solid #FCD34D", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 4 }}>⚠️ Duplicate Detected</div>
              <div style={{ fontSize: 12, color: "#78350F", marginBottom: 10 }}>A record with the same key fields already exists. Do you want to save it anyway?</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setDupWarning(false)} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "1.5px solid #FCD34D", background: "#fff", color: "#92400E", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Go Back</button>
                <button onClick={() => handleSave(true)} style={{ flex: 1, padding: "7px 0", borderRadius: 8, border: "none", background: "#D97706", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Save Anyway</button>
              </div>
            </div>
          )}
          {editFields.map(f => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <label style={labelSt}>{f.label}</label>
              {f.inputType === "textarea" ? (
                <textarea value={form[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} rows={3} style={{ ...inputSt, resize: "vertical", lineHeight: 1.5 }} />
              ) : f.inputType === "select" ? (
                <select value={form[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} style={{ ...inputSt, cursor: "pointer" }}>
                  <option value="">— select —</option>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.inputType} value={form[f.key] ?? ""} onChange={e => set(f.key, e.target.value)} style={inputSt} />
              )}
            </div>
          ))}
        </div>
        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #F0F0F0", display: "flex", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: "11px 0", borderRadius: 10, border: "none", background: saving ? "#9CA3AF" : isNew ? "linear-gradient(135deg,#107C10,#0B5C0B)" : "linear-gradient(135deg,#0052CC,#003DA6)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", boxShadow: saving ? "none" : isNew ? "0 4px 14px rgba(16,124,16,0.3)" : "0 4px 14px rgba(0,82,204,0.3)" }}>
            {saving ? "Saving..." : isNew ? "✅ Add Record" : "💾 Save Changes"}
          </button>
        </div>
      </div>
    </>
  );
}

const PAGE_SIZE = 15;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReportsPage({ transactions = {}, onEdit, onDelete, onAdd }) {
  const [activeTab, setActiveTab]   = useState(TABS[0].key);
  const [preset, setPreset]         = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo]     = useState("");
  const [search, setSearch]         = useState("");
  const [sortField, setSortField]   = useState("date");
  const [sortDir, setSortDir]       = useState("desc");
  const [page, setPage]             = useState(0);
  const [editRecord, setEditRecord] = useState(null);
  const [isNewRecord, setIsNewRecord] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [subFilter, setSubFilter] = useState("all"); // location (diesel) or route (logistics)
  const [showCharts, setShowCharts] = useState(true);
  const [exportPickerOpen, setExportPickerOpen] = useState(false);
  const [exportSelected, setExportSelected] = useState(() => new Set(TABS.map(t => t.key)));
  const [fsChart, setFsChart] = useState(null); // { title, type, data, series }

  const tab = TABS.find(t => t.key === activeTab) || TABS[0];

  // Date range
  const { from: rangeFrom, to: rangeTo } = useMemo(() => {
    if (preset === "custom") return { from: customFrom, to: customTo };
    const p = PRESETS.find(p => p.key === preset);
    return { from: p?.from || "", to: "" };
  }, [preset, customFrom, customTo]);

  const rangeLabel = useMemo(() => {
    const p = PRESETS.find(p => p.key === preset);
    if (preset === "custom") return `${customFrom || "?"} – ${customTo || "?"}`;
    return p?.label || "All Time";
  }, [preset, customFrom, customTo]);

  // Raw rows for active tab (date-filtered)
  const rawRows = useMemo(() => {
    const arr = transactions[tab.collectionKey] || [];
    const df = tab.dateField;
    return arr.filter(r => inRange(r[df] || r.weekEnding, rangeFrom, rangeTo));
  }, [transactions, tab, rangeFrom, rangeTo]);

  // Sub-filter options: locations for diesel, routes for logistics
  const subFilterOptions = useMemo(() => {
    if (tab.key === "dieselRecords")
      return ["all", ...Array.from(new Set(rawRows.map(r => r.location).filter(Boolean))).sort()];
    if (tab.key === "logisticsRecords")
      return ["all", ...Array.from(new Set(rawRows.map(r => r.route).filter(Boolean))).sort()];
    return [];
  }, [tab.key, rawRows]);

  // Search + location filter + sort + paginate
  const filtered = useMemo(() => {
    let data = [...rawRows];
    if (tab.key === "dieselRecords" && subFilter !== "all")
      data = data.filter(r => r.location === subFilter);
    if (tab.key === "logisticsRecords" && subFilter !== "all")
      data = data.filter(r => r.route === subFilter);
    if (search) {
      const s = search.toLowerCase();
      data = data.filter(r => Object.values(r).some(v => String(v).toLowerCase().includes(s)));
    }
    data.sort((a, b) => {
      const av = a[sortField] ?? "";
      const bv = b[sortField] ?? "";
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return data;
  }, [rawRows, tab.key, subFilter, search, sortField, sortDir]);

  const pages    = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Summary stats (based on location-filtered rows so cards update too)
  const stats = useMemo(() => tab.summary(filtered, transactions, rangeFrom, rangeTo), [tab, filtered, transactions, rangeFrom, rangeTo]);

  // Reset page on tab/filter change
  const switchTab = (key) => {
    setActiveTab(key); setPage(0); setSearch("");
    setSortField("date"); setSortDir("desc"); setSubFilter("all");
  };
  const toggleSort = (key) => {
    if (sortField === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(key); setSortDir("asc"); }
    setPage(0);
  };

  // Export active tab to Excel
  const exportTab = () => {
    if (!filtered.length) return;
    const rows = filtered.map(r => {
      const out = {};
      tab.columns.forEach(c => { out[c.label] = r[c.key] ?? ""; });
      return out;
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = tab.columns.map(c => ({ wch: Math.max(c.label.length + 4, 18) }));
    XLSX.utils.book_append_sheet(wb, ws, tab.label);
    XLSX.writeFile(wb, `LekOil_${tab.label.replace(/ /g,"_")}_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  // Export all tabs as a self-contained tabbed HTML report
  const exportAllToHTML = () => {
    const dateStr = new Date().toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
    const timeStr = new Date().toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" });

    const sections = TABS.filter(t => exportSelected.has(t.key)).map(t => {
      const arr = transactions[t.collectionKey] || [];
      const df  = t.dateField;
      const rows = arr.filter(r => inRange(r[df] || r.weekEnding, rangeFrom, rangeTo));
      return { tab: t, rows, stats: t.summary(rows) };
    }).filter(s => s.rows.length > 0);

    // Resolve actual start/end dates for the report period
    const fmtDateFull = (iso) => {
      if (!iso) return null;
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" });
    };
    // Collect every date value across all included rows to determine true bounds
    const allDates = sections.flatMap(({ tab: t, rows }) =>
      rows.map(r => (r[t.dateField] || r.weekEnding || "").substring(0, 10)).filter(Boolean)
    ).sort();
    const dataStart = allDates[0] || null;
    const dataEnd   = allDates[allDates.length - 1] || null;
    // If a explicit range was set, use it; otherwise fall back to data bounds
    const periodStart = rangeFrom || dataStart;
    const periodEnd   = rangeTo   || dataEnd || today.toISOString().split("T")[0];
    const periodStartFmt = fmtDateFull(periodStart) || "—";
    const periodEndFmt   = fmtDateFull(periodEnd)   || "—";

    const badgeCSS = Object.entries(BADGE).map(([k, v]) =>
      `.badge-${k.replace(/[^a-zA-Z0-9]/g,"_")}{background:${v.bg};color:${v.color}}`
    ).join("\n");

    const fmtVal = (value, type) => {
      if (value == null || value === "") return '<span style="color:#ccc">—</span>';
      if (type === "currency")     return `<span style="font-weight:700;color:#0052CC">$${Number(value).toLocaleString()}</span>`;
      if (type === "currency_ngn") return `<span style="font-weight:700;color:#0052CC">₦${Number(value).toLocaleString()}</span>`;
      if (type === "currency$")    return value > 0 ? `<span style="font-weight:700;color:#059669">$${Number(value).toLocaleString()}</span>` : '<span style="color:#ccc">—</span>';
      if (type === "number")     return `<span style="font-weight:600">${Number(value).toLocaleString()}</span>`;
      if (type === "date")       return `<span style="white-space:nowrap;font-size:12px">${formatDate(value) || value}</span>`;
      if (type === "percentage") return `<span style="font-weight:600;color:#0052CC">${Math.round(Number(value) * 100)}%</span>`;
      if (type === "score") {
        const n = Number(value);
        const c = n >= 85 ? "#15803D" : n >= 70 ? "#D97706" : "#DC2626";
        return `<span style="font-weight:700;color:${c};background:${c}18;padding:2px 8px;border-radius:6px;font-size:12px">${n}</span>`;
      }
      if (type === "badge") return `<span class="badge badge-${String(value).replace(/[^a-zA-Z0-9]/g,"_")}">${value}</span>`;
      const s = String(value);
      return `<span title="${s.replace(/"/g,"&quot;")}" style="max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;font-size:12px">${s}</span>`;
    };

    const fmtStat = (s) => {
      if (s.fmt === "$")    return `$${Number(s.value).toLocaleString()}`;
      if (s.fmt === "₦")    return `₦${Number(s.value).toLocaleString()}`;
      if (s.fmt === "L")    return `${Number(s.value).toLocaleString()} L`;
      if (s.fmt === "days") return `${s.value} d`;
      if (s.fmt === "n")    return Number(s.value).toLocaleString();
      return s.value;
    };

    // Build SVG charts per section for the HTML export
    let _chartIdCounter = 0;
    const buildSectionCharts = (t, rows) => {
      const cfgs = TAB_CHARTS[t.key]?.charts || [];
      if (!cfgs.length || rows.length < 2) return "";
      const chartHTMLs = cfgs.map(cfg => {
        const { data, series } = cfg.getData(rows, t.dateField);
        if (!data || data.length < 2) return "";
        const svg = buildSVGChart(cfg, data, series, 520, 200);
        if (!svg) return "";
        const cid = `chart-${++_chartIdCounter}`;
        return `
          <div class="chart-card">
            <div class="chart-title">${cfg.title}</div>
            <button class="chart-fs-btn" onclick="openChart('${cid}','${cfg.title.replace(/'/g,"\\'")}')">&#x26F6; Full Screen</button>
            <div id="${cid}">${svg}</div>
          </div>`;
      }).filter(Boolean).join("");
      if (!chartHTMLs) return "";
      return `
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(400px,1fr));gap:16px;margin-bottom:20px">
          ${chartHTMLs}
        </div>`;
    };

    // One <div class="tab-panel"> per section — only the active one is visible
    const panelsHTML = sections.map(({ tab: t, rows, stats }, i) => `
      <div class="tab-panel" id="panel-${t.key}" style="display:${i === 0 ? "block" : "none"}">
        <!-- Section header -->
        <div style="display:flex;align-items:center;gap:14px;margin-bottom:18px">
          <div style="width:44px;height:44px;border-radius:12px;background:linear-gradient(135deg,#001A3D,#0052CC);display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0">${t.icon}</div>
          <div>
            <div style="font-size:18px;font-weight:800;color:#111827">${t.label}</div>
            <div style="font-size:12px;color:#9CA3AF">${rows.length} record${rows.length !== 1 ? "s" : ""} &nbsp;·&nbsp; ${rangeLabel}</div>
          </div>
        </div>
        <!-- Summary stats -->
        <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:20px">
          ${stats.map(s => `
            <div style="background:#fff;border-radius:12px;padding:14px 18px;border:1px solid #EAECEF;box-shadow:0 2px 8px rgba(0,0,0,0.04);position:relative;overflow:hidden;min-width:110px">
              <div style="position:absolute;top:0;left:0;right:0;height:3px;background:${s.accent};border-radius:12px 12px 0 0"></div>
              <div style="font-size:19px;font-weight:800;color:#111827;line-height:1.2;margin-top:4px">${fmtStat(s)}</div>
              <div style="font-size:11px;color:#9CA3AF;margin-top:4px;font-weight:500">${s.label}</div>
            </div>`).join("")}
        </div>
        <!-- Charts -->
        ${buildSectionCharts(t, rows)}
        <!-- Notes & Highlights: merged-by-week card layout -->
        ${t.key === "processNotes" ? (() => {
          const byWeek = {};
          rows.forEach(row => { const w = row.weekEnding || ""; if (!byWeek[w]) byWeek[w] = []; byWeek[w].push(row); });
          const weeks = Object.keys(byWeek).sort((a, b) => b.localeCompare(a));
          const esc = s => String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
          return `<div class="notes-grid">${weeks.map(week => {
            const wRows = byWeek[week];
            const allH = wRows.map(r => r.highlights).filter(h => h && h.trim()).join("\n\n");
            const allT = wRows.map(r => r.text).filter(t => t && t.trim()).join("\n\n");
            return `<div class="note-card">
              <div class="note-week">📌 Week ending: ${fmtVal(week, "date")}</div>
              ${allH ? `<div class="note-section"><div class="note-label">🌟 Weekly Highlights</div><div class="note-body">${esc(allH)}</div></div>` : ""}
              ${allT ? `<div class="note-section"><div class="note-label">⚙️ Process Improvement</div><div class="note-body">${esc(allT)}</div></div>` : ""}
            </div>`;
          }).join("")}</div>`;
        })() : `
        <!-- Standard table -->
        <div class="tbl-wrap">
          <table>
            <thead><tr>
              <th style="width:40px;text-align:center">#</th>
              ${t.columns.map(c => `<th>${c.label}</th>`).join("")}
            </tr></thead>
            <tbody>
              ${rows.map((row, ri) => `
                <tr class="dr" style="background:${ri % 2 === 0 ? "#fff" : "#FAFBFD"}">
                  <td style="text-align:center;color:#9CA3AF;font-size:11px;font-weight:600">${ri + 1}</td>
                  ${t.columns.map(c => `<td>${fmtVal(row[c.key], c.type)}</td>`).join("")}
                </tr>`).join("")}
            </tbody>
          </table>
        </div>`}
      </div>`).join("");

    // Tab bar buttons
    const tabBarHTML = sections.map(({ tab: t, rows }, i) => `
      <button class="tb${i === 0 ? " tb-active" : ""}" onclick="showTab('${t.key}')" data-key="${t.key}">
        <span>${t.icon}</span>
        <span>${t.label}</span>
        <span class="tb-count">${rows.length}</span>
      </button>`).join("");

    const firstKey = sections[0]?.tab.key || "";

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>LekOil SCM Report · ${dateStr}</title>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',system-ui,sans-serif;background:#F8F9FB;color:#111827;min-height:100vh}

/* Header */
.hdr{background:linear-gradient(135deg,#001A3D,#003080);color:#fff;padding:20px 28px;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;box-shadow:0 4px 20px rgba(0,0,0,0.25)}
.hdr-title{font-size:20px;font-weight:800;margin-bottom:3px}
.hdr-sub{font-size:12px;color:rgba(255,255,255,0.65)}
.hdr-badges{display:flex;gap:10px;flex-wrap:wrap}
.hdr-badge{background:rgba(255,255,255,0.13);border-radius:10px;padding:8px 16px;text-align:center}
.hdr-badge-val{font-size:20px;font-weight:800}
.hdr-badge-lbl{font-size:10px;color:rgba(255,255,255,0.55);text-transform:uppercase;letter-spacing:.8px;margin-top:2px}

/* Tab bar */
.tabbar{background:linear-gradient(135deg,#001A3D,#00256B);padding:6px 16px;display:flex;gap:4px;overflow-x:auto;scrollbar-width:none;position:sticky;top:0;z-index:50;box-shadow:0 2px 12px rgba(0,0,0,0.2)}
.tabbar::-webkit-scrollbar{display:none}
.tb{display:flex;align-items:center;gap:6px;padding:8px 14px;border-radius:10px;border:none;cursor:pointer;font-family:inherit;background:transparent;color:rgba(255,255,255,0.6);font-weight:500;font-size:12px;white-space:nowrap;transition:all .15s}
.tb:hover{background:rgba(255,255,255,0.1);color:#fff}
.tb-active{background:rgba(255,255,255,0.18)!important;color:#fff!important;font-weight:700;box-shadow:0 2px 8px rgba(0,0,0,0.2)}
.tb-count{background:rgba(255,255,255,0.15);border-radius:20px;padding:1px 7px;font-size:10px;font-weight:700}
.tb-active .tb-count{background:rgba(255,255,255,0.3)}

/* Content — full viewport width, no centering cap */
.content{width:100%;padding:28px 32px;box-sizing:border-box}

/* Tables — stretch to full available width */
.tbl-wrap{overflow-x:auto;border-radius:12px;border:1px solid #EAECEF;box-shadow:0 4px 16px rgba(0,0,0,0.05);width:100%}
table{border-collapse:collapse;width:100%;min-width:600px;font-size:13px;table-layout:auto}
th{padding:10px 14px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;background:linear-gradient(135deg,#001A3D,#003080);color:rgba(255,255,255,0.9);border-right:1px solid rgba(255,255,255,0.08)}
td{padding:9px 14px;border-bottom:1px solid #F0F2F5;border-right:1px solid #F0F2F5;vertical-align:top;word-break:break-word;max-width:320px;text-transform:uppercase}
td span[style*="font-weight:700"]{text-transform:none} /* keep currency/numbers as-is */
td .badge{text-transform:uppercase}

/* Chart cards */
.chart-card{background:#fff;border-radius:14px;padding:18px 20px;border:1px solid #EAECEF;box-shadow:0 2px 10px rgba(0,0,0,0.05);position:relative}
.chart-title{font-size:13px;font-weight:700;color:#111827;margin-bottom:14px;padding-right:88px}
.chart-fs-btn{position:absolute;top:14px;right:14px;display:flex;align-items:center;gap:5px;padding:5px 12px;background:#EBF3FF;border:1.5px solid #C7E0FF;border-radius:8px;cursor:pointer;font-size:11px;font-weight:700;color:#0052CC;font-family:inherit;transition:all .15s;white-space:nowrap}
.chart-fs-btn:hover{background:#0052CC;color:#fff;border-color:#0052CC}

/* Fullscreen modal */
.fs-overlay{display:none;position:fixed;inset:0;z-index:9999;background:rgba(0,10,28,0.88);backdrop-filter:blur(6px);align-items:center;justify-content:center;padding:40px}
.fs-overlay.open{display:flex}
.fs-box{background:#fff;border-radius:20px;padding:28px 32px;width:100%;max-width:1200px;max-height:92vh;overflow:auto;position:relative;box-shadow:0 32px 80px rgba(0,0,0,0.4)}
.fs-box svg{width:100%!important;height:auto!important;display:block}
.fs-close{position:absolute;top:16px;right:16px;width:36px;height:36px;border-radius:10px;border:1.5px solid #E5E7EB;background:#F9FAFB;cursor:pointer;font-size:18px;display:flex;align-items:center;justify-content:center;color:#374151;transition:all .15s}
.fs-close:hover{background:#FEF2F2;border-color:#FECACA;color:#EF4444}
.fs-title{font-size:16px;font-weight:800;color:#111827;margin-bottom:20px;padding-right:44px}

/* Notes cards */
.notes-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(360px,1fr));gap:16px;margin-bottom:8px}
.note-card{background:#fff;border-radius:14px;padding:20px 22px;border:1px solid #EAECEF;box-shadow:0 2px 10px rgba(0,0,0,0.05)}
.note-week{font-size:11px;font-weight:700;color:#0052CC;text-transform:uppercase;letter-spacing:.7px;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #EBF3FF}
.note-section{margin-top:10px}
.note-label{font-size:10px;font-weight:700;color:#9CA3AF;text-transform:uppercase;letter-spacing:.8px;margin-bottom:4px}
.note-body{font-size:13px;color:#374151;line-height:1.65;white-space:pre-wrap}

.dr:hover td{background:#EEF5FF!important}

/* Badges */
.badge{padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;white-space:nowrap}
${badgeCSS}

@media(max-width:768px){
  .content{padding:16px 12px}
  .notes-grid{grid-template-columns:1fr}
}
@media print{
  .tabbar{display:none}
  .tab-panel{display:block!important}
  .dr:hover td{background:inherit!important}
  .notes-grid{grid-template-columns:repeat(2,1fr)}
}
</style>
</head>
<body>

<header class="hdr">
  <div>
    <div class="hdr-title">🛢 LekOil SCM Transaction Report</div>
    <div class="hdr-sub">Generated ${dateStr} at ${timeStr} &nbsp;·&nbsp; Confidential</div>
    <!-- Report period band -->
    <div style="margin-top:10px;display:inline-flex;align-items:center;gap:10px;background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.18);border-radius:10px;padding:8px 16px">
      <span style="font-size:14px">📅</span>
      <div>
        <div style="font-size:10px;color:rgba(255,255,255,0.55);text-transform:uppercase;letter-spacing:.9px;margin-bottom:2px">Report Period</div>
        <div style="font-size:15px;font-weight:800;letter-spacing:.3px">${periodStartFmt} &nbsp;→&nbsp; ${periodEndFmt}</div>
        <div style="font-size:10px;color:rgba(255,255,255,0.55);margin-top:1px">${rangeLabel}</div>
      </div>
    </div>
  </div>
  <div class="hdr-badges">
    <div class="hdr-badge">
      <div class="hdr-badge-val">${sections.length}</div>
      <div class="hdr-badge-lbl">Categories</div>
    </div>
    <div class="hdr-badge">
      <div class="hdr-badge-val">${sections.reduce((s,x)=>s+x.rows.length,0)}</div>
      <div class="hdr-badge-lbl">Total Records</div>
    </div>
  </div>
</header>

<div class="tabbar">${tabBarHTML}</div>

<main class="content">${panelsHTML}</main>

<!-- Fullscreen chart overlay -->
<div class="fs-overlay" id="fs-overlay" onclick="if(event.target===this)closeChart()">
  <div class="fs-box" id="fs-box">
    <button class="fs-close" onclick="closeChart()">&#x2715;</button>
    <div class="fs-title" id="fs-title"></div>
    <div id="fs-content"></div>
  </div>
</div>

<script>
function showTab(key){
  document.querySelectorAll('.tab-panel').forEach(p=>p.style.display='none');
  document.querySelectorAll('.tb').forEach(b=>{
    b.classList.toggle('tb-active', b.dataset.key===key);
  });
  var p=document.getElementById('panel-'+key);
  if(p) p.style.display='block';
  window.scrollTo({top:0,behavior:'smooth'});
}

// Chart fullscreen
function openChart(id, title){
  var src=document.getElementById(id);
  if(!src) return;
  document.getElementById('fs-title').textContent=title;
  document.getElementById('fs-content').innerHTML=src.innerHTML;
  document.getElementById('fs-overlay').classList.add('open');
  document.body.style.overflow='hidden';
}
function closeChart(){
  document.getElementById('fs-overlay').classList.remove('open');
  document.body.style.overflow='';
}

// Keyboard: Escape closes modal, left/right arrows switch tabs
var _keys=${JSON.stringify(sections.map(s=>s.tab.key))};
var _cur='${firstKey}';
document.addEventListener('keydown',function(e){
  if(e.key==='Escape'){ closeChart(); return; }
  if(document.getElementById('fs-overlay').classList.contains('open')) return;
  if(e.key==='ArrowRight'||e.key==='ArrowLeft'){
    var i=_keys.indexOf(_cur);
    _cur=e.key==='ArrowRight'?_keys[Math.min(i+1,_keys.length-1)]:_keys[Math.max(i-1,0)];
    showTab(_cur);
  }
});
</script>
</body>
</html>`;

    const blob = new Blob([html], { type:"text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `LekOil_SCM_Report_${periodStart || "all"}_to_${periodEnd || "all"}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const confirmDelete = () => {
    if (deleteTarget) { onDelete?.(tab.collectionKey, deleteTarget.id); setDeleteTarget(null); }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI',system-ui,sans-serif" }}>
      <style>{`
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes slideInRight { from{transform:translateX(100%);opacity:0} to{transform:translateX(0);opacity:1} }
        .tr-row:hover { background: #EEF5FF !important; }
        .tr-row:hover .row-act { opacity: 1 !important; }
        .row-act { opacity: 0; transition: opacity 0.15s; }
        .tab-pill:hover { background: rgba(255,255,255,0.12) !important; }
        .th-btn:hover { background: rgba(255,255,255,0.08) !important; }
        .pg-btn:hover:not(:disabled) { background: #EBF3FF !important; color: #0052CC !important; }
      `}</style>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 20, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,#001A3D,#0052CC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>📂</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>Transaction Register</div>
            <div style={{ fontSize: 12, color: "#9CA3AF" }}>
              {tab.icon} {tab.label} · {rawRows.length} records · {rangeLabel}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => setShowCharts(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", background: showCharts ? "#EBF3FF" : "#F3F4F6", color: showCharts ? "#0052CC" : "#6B7280", border: `1.5px solid ${showCharts ? "#C7E0FF" : "#E5E7EB"}`, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
          >{showCharts ? "📊 Hide Charts" : "📊 Show Charts"}</button>
          <button
            onClick={() => setExportPickerOpen(true)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", background: "linear-gradient(135deg,#001A3D,#003080)", color: "#fff", border: "none", borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 14px rgba(0,26,61,0.3)", whiteSpace: "nowrap" }}
          >🌐 Export Full Report</button>
          <button
            onClick={exportTab}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 18px", background: "linear-gradient(135deg,#107C10,#0B5C0B)", color: "#fff", border: "none", borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 14px rgba(16,124,16,0.25)", whiteSpace: "nowrap" }}
          >↓ Export {tab.label}</button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ background: "linear-gradient(135deg,#001A3D,#003080)", borderRadius: 14, padding: "6px 8px", marginBottom: 20, overflowX: "auto", display: "flex", gap: 4, scrollbarWidth: "none" }}>
        {TABS.map(t => {
          const count = (transactions[t.collectionKey] || []).length;
          const isActive = t.key === activeTab;
          return (
            <button
              key={t.key}
              className="tab-pill"
              onClick={() => switchTab(t.key)}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                borderRadius: 10, border: "none", cursor: "pointer", fontFamily: "inherit",
                background: isActive ? "rgba(255,255,255,0.18)" : "transparent",
                color: isActive ? "#fff" : "rgba(255,255,255,0.6)",
                fontWeight: isActive ? 700 : 500, fontSize: 12, whiteSpace: "nowrap",
                transition: "all 0.15s",
                boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.2)" : "none",
              }}
            >
              <span>{t.icon}</span>
              <span>{t.label}</span>
              <span style={{ background: isActive ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)", borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ── Sub-filter: location (diesel) or route (logistics) ── */}
      {subFilterOptions.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>
            {tab.key === "dieselRecords" ? "⛽ Location:" : "🚚 Route:"}
          </span>
          {subFilterOptions.map(opt => (
            <button key={opt} onClick={() => { setSubFilter(opt); setPage(0); }}
              style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${subFilter === opt ? "#0052CC" : "#E5E7EB"}`, background: subFilter === opt ? "#0052CC" : "#fff", color: subFilter === opt ? "#fff" : "#374151", fontSize: 12, fontWeight: subFilter === opt ? 700 : 500, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
              {opt === "all" ? (tab.key === "dieselRecords" ? "All Locations" : "All Routes") : opt}
            </button>
          ))}
        </div>
      )}

      {/* ── Date filter ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>Period:</span>
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => { setPreset(p.key); setPage(0); }}
            style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${preset === p.key ? "#0052CC" : "#E5E7EB"}`, background: preset === p.key ? "#0052CC" : "#fff", color: preset === p.key ? "#fff" : "#374151", fontSize: 12, fontWeight: preset === p.key ? 700 : 500, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
            {p.label}
          </button>
        ))}
        <button onClick={() => { setPreset("custom"); setPage(0); }}
          style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${preset === "custom" ? "#0052CC" : "#E5E7EB"}`, background: preset === "custom" ? "#0052CC" : "#fff", color: preset === "custom" ? "#fff" : "#374151", fontSize: 12, fontWeight: preset === "custom" ? 700 : 500, cursor: "pointer", fontFamily: "inherit" }}>
          Custom
        </button>
        {preset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPage(0); }} style={{ padding: "5px 10px", border: "1.5px solid #C7E0FF", borderRadius: 8, fontSize: 12, fontFamily: "inherit", color: "#111827", outline: "none" }} />
            <span style={{ color: "#9CA3AF" }}>—</span>
            <input type="date" value={customTo} onChange={e => { setCustomTo(e.target.value); setPage(0); }} style={{ padding: "5px 10px", border: "1.5px solid #C7E0FF", borderRadius: 8, fontSize: 12, fontFamily: "inherit", color: "#111827", outline: "none" }} />
          </>
        )}
      </div>

      {/* ── Summary strip ── */}
      {stats.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {stats.map((s, i) => <SummaryCard key={i} {...s} />)}
        </div>
      )}

      {/* ── Tab Charts ── */}
      {showCharts && (
        <TabCharts tabKey={tab.key} rows={rawRows} dateField={tab.dateField} onFullscreen={setFsChart} />
      )}

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 340 }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#9CA3AF", pointerEvents: "none" }}>🔍</span>
          <input
            value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder={`Search ${tab.label.toLowerCase()}...`}
            style={{ width: "100%", padding: "9px 32px 9px 34px", fontSize: 13, fontFamily: "inherit", background: "#fff", color: "#111827", border: "1.5px solid #E5E7EB", borderRadius: 9, outline: "none", boxSizing: "border-box" }}
          />
          {search && <button onClick={() => setSearch("")} style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 16 }}>×</button>}
        </div>
        {tab.key !== "processNotes" && (
          <button
            onClick={() => { setIsNewRecord(true); setEditRecord({ _placeholder: true }); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", background: "linear-gradient(135deg,#107C10,#0B5C0B)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 3px 10px rgba(16,124,16,0.25)", whiteSpace: "nowrap" }}
          >＋ Add New</button>
        )}
        {tab.key === "processNotes" && (
          <button
            onClick={() => { setIsNewRecord(true); setEditRecord({ _placeholder: true }); }}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", background: "linear-gradient(135deg,#107C10,#0B5C0B)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 3px 10px rgba(16,124,16,0.25)", whiteSpace: "nowrap" }}
          >＋ Add Note</button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#9CA3AF", fontWeight: 500 }}>
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          {search && ` matching "${search}"`}
        </div>
      </div>

      {/* ── Notes & Highlights: merged-by-week card layout ── */}
      {tab.key === "processNotes" ? (
        <div>
          {filtered.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #EAECEF", padding: "56px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 4 }}>No notes yet</div>
              <div style={{ fontSize: 12, color: "#9CA3AF" }}>{search ? `No matches for "${search}"` : "No records for the selected period."}</div>
            </div>
          ) : (() => {
            // Group all filtered rows by weekEnding into one card per week
            const byWeek = {};
            filtered.forEach(row => {
              const w = row.weekEnding || "";
              if (!byWeek[w]) byWeek[w] = [];
              byWeek[w].push(row);
            });
            const weeks = Object.keys(byWeek).sort((a, b) => b.localeCompare(a));
            const btnSt = (color, bg, border) => ({ width: 24, height: 24, borderRadius: 6, border: `1px solid ${border}`, background: bg, cursor: "pointer", fontSize: 11, color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 });
            return (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))", gap: 16 }}>
                {weeks.map(week => {
                  const rows = byWeek[week];
                  return (
                    <div key={week} style={{ background: "#fff", borderRadius: 14, padding: "20px 22px", border: "1px solid #EAECEF", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
                      {/* Week heading */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#0052CC", textTransform: "uppercase", letterSpacing: 0.7, marginBottom: 12, paddingBottom: 8, borderBottom: "2px solid #EBF3FF" }}>
                        📌 Week ending: {formatDate(week)}
                      </div>
                      {/* One section per row — both blocks + their own buttons */}
                      {rows.map((row, ri) => (
                        <div key={row.id || ri} style={{ marginBottom: ri < rows.length - 1 ? 14 : 0, paddingBottom: ri < rows.length - 1 ? 14 : 0, borderBottom: ri < rows.length - 1 ? "1px dashed #E5E7EB" : "none" }}>
                          {row.highlights && row.highlights.trim() && (
                            <div style={{ marginBottom: row.text && row.text.trim() ? 10 : 0 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>🌟 Weekly Highlights</div>
                                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                  <button onClick={() => { setIsNewRecord(false); setEditRecord(row); }} title="Edit" style={btnSt("#0052CC","#EBF3FF","#C7E0FF")}>✏️</button>
                                  <button onClick={() => setDeleteTarget(row)} title="Delete" style={btnSt("#EF4444","#FEF2F2","#FECACA")}>🗑</button>
                                </div>
                              </div>
                              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{row.highlights}</div>
                            </div>
                          )}
                          {row.text && row.text.trim() && (
                            <div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.8 }}>⚙️ Process Improvement</div>
                                {/* only show buttons here if highlights didn't already show them for this row */}
                                {!(row.highlights && row.highlights.trim()) && (
                                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                                    <button onClick={() => { setIsNewRecord(false); setEditRecord(row); }} title="Edit" style={btnSt("#0052CC","#EBF3FF","#C7E0FF")}>✏️</button>
                                    <button onClick={() => setDeleteTarget(row)} title="Delete" style={btnSt("#EF4444","#FEF2F2","#FECACA")}>🗑</button>
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{row.text}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      ) : (

      /* ── Standard table for all other tabs ── */
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #EAECEF", boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "max-content", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "linear-gradient(135deg,#001A3D,#003080)" }}>
                <th style={{ ...thSt, minWidth: 40, textAlign: "center" }}>#</th>
                {tab.columns.map(col => (
                  <th key={col.key} className="th-btn" onClick={() => toggleSort(col.key)}
                    style={{ ...thSt, minWidth: col.w, cursor: "pointer" }}>
                    <span>{col.label}</span>
                    <span style={{ marginLeft: 4, display: "inline-flex", flexDirection: "column", gap: 1, verticalAlign: "middle" }}>
                      <span style={{ fontSize: 7, lineHeight: 1, color: sortField === col.key && sortDir === "asc" ? "#60A5FA" : "rgba(255,255,255,0.3)" }}>▲</span>
                      <span style={{ fontSize: 7, lineHeight: 1, color: sortField === col.key && sortDir === "desc" ? "#60A5FA" : "rgba(255,255,255,0.3)" }}>▼</span>
                    </span>
                  </th>
                ))}
                <th style={{ ...thSt, minWidth: 80, textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={tab.columns.length + 2} style={{ padding: "56px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#374151", marginBottom: 4 }}>No {tab.label} records</div>
                    <div style={{ fontSize: 12, color: "#9CA3AF" }}>
                      {search ? `No matches for "${search}"` : "No records for the selected period."}
                    </div>
                  </td>
                </tr>
              ) : pageData.map((row, i) => (
                <tr key={row.id || i} className="tr-row"
                  style={{ background: i % 2 === 0 ? "#fff" : "#FAFBFD", borderBottom: "1px solid #F3F4F6", cursor: "pointer", transition: "background 0.1s" }}
                  onClick={() => { setIsNewRecord(false); setEditRecord(row); }}
                >
                  <td style={{ ...tdSt, textAlign: "center", color: "#9CA3AF", fontSize: 11, fontWeight: 600 }}>
                    {page * PAGE_SIZE + i + 1}
                  </td>
                  {tab.columns.map(col => (
                    <td key={col.key} style={{ ...tdSt, maxWidth: col.w + 40 }}>
                      {formatCell(row[col.key], col.type)}
                    </td>
                  ))}
                  <td style={{ ...tdSt, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                    <div className="row-act" style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                      <button onClick={() => { setIsNewRecord(false); setEditRecord(row); }} title="Edit"
                        style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #C7E0FF", background: "#EBF3FF", cursor: "pointer", fontSize: 13, color: "#0052CC", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>✏️</button>
                      <button onClick={() => setDeleteTarget(row)} title="Delete"
                        style={{ width: 28, height: 28, borderRadius: 7, border: "1px solid #FECACA", background: "#FEF2F2", cursor: "pointer", fontSize: 13, color: "#EF4444", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #F3F4F6", background: "#FAFBFC", flexWrap: "wrap", gap: 10 }}>
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>
              Showing <strong style={{ color: "#374151" }}>{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: "#374151" }}>{filtered.length}</strong>
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {[["«", () => setPage(0)], ["‹", () => setPage(p => Math.max(0, p - 1))]].map(([l, fn]) => (
                <button key={l} className="pg-btn" onClick={fn} disabled={page === 0} style={{ ...pgSt, opacity: page === 0 ? 0.35 : 1 }}>{l}</button>
              ))}
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const p = Math.max(0, Math.min(page - 2, pages - 5)) + i;
                return <button key={p} className="pg-btn" onClick={() => setPage(p)} style={{ ...pgSt, background: p === page ? "#0052CC" : "#fff", color: p === page ? "#fff" : "#374151", borderColor: p === page ? "#0052CC" : "#E5E7EB", fontWeight: p === page ? 700 : 500 }}>{p + 1}</button>;
              })}
              {[["›", () => setPage(p => Math.min(pages - 1, p + 1))], ["»", () => setPage(pages - 1)]].map(([l, fn]) => (
                <button key={l} className="pg-btn" onClick={fn} disabled={page >= pages - 1} style={{ ...pgSt, opacity: page >= pages - 1 ? 0.35 : 1 }}>{l}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      )} {/* end processNotes conditional */}

      <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 10, textAlign: "center" }}>
        💡 Click any row to edit · Hover to reveal delete · ＋ Add New to create a record
      </p>

      {/* ── Fullscreen chart modal ── */}
      {fsChart && (
        <div
          onClick={() => setFsChart(null)}
          style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,10,28,0.88)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 20, padding: "28px 32px", width: "100%", maxWidth: 1100, maxHeight: "90vh", overflow: "auto", position: "relative", boxShadow: "0 32px 80px rgba(0,0,0,0.4)" }}
          >
            <button onClick={() => setFsChart(null)} style={{ position: "absolute", top: 16, right: 16, width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#F9FAFB", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "#374151" }}>×</button>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#111827", marginBottom: 20, paddingRight: 44 }}>{fsChart.title}</div>
            <ChartBody type={fsChart.type} data={fsChart.data} series={fsChart.series} height={500} />
          </div>
        </div>
      )}

      {/* ── Edit / Add Drawer ── */}
      <EditDrawer
        record={editRecord}
        isNew={isNewRecord}
        collectionKey={tab.collectionKey}
        editFields={tab.editFields}
        existingRows={transactions[tab.collectionKey] || []}
        onClose={() => { setEditRecord(null); setIsNewRecord(false); }}
        onSave={(col, id, data) => { onEdit?.(col, id, data); }}
        onAdd={(col, data) => { onAdd?.(col, data); }}
      />

      {/* ── Delete Confirm ── */}
      {/* ── Export Picker Modal ── */}
      {exportPickerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,15,40,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 26px", maxWidth: 520, width: "100%", boxShadow: "0 24px 60px rgba(0,0,0,0.22)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg,#001A3D,#0052CC)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🌐</div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>Select Reports to Export</div>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>Choose which tabs to include in the HTML report</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
              {TABS.map(t => {
                const count = (transactions[t.collectionKey] || []).length;
                const checked = exportSelected.has(t.key);
                return (
                  <label key={t.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${checked ? "#0052CC" : "#E5E7EB"}`, background: checked ? "#EBF3FF" : "#F9FAFB", cursor: "pointer", transition: "all 0.12s" }}>
                    <input type="checkbox" checked={checked} onChange={() => setExportSelected(prev => { const s = new Set(prev); if (s.has(t.key)) s.delete(t.key); else s.add(t.key); return s; })} style={{ width: 15, height: 15, accentColor: "#0052CC", cursor: "pointer" }} />
                    <span style={{ fontSize: 14 }}>{t.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: checked ? 700 : 500, color: checked ? "#0052CC" : "#374151", flex: 1 }}>{t.label}</span>
                    <span style={{ fontSize: 11, color: "#9CA3AF" }}>{count}</span>
                  </label>
                );
              })}
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 8 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setExportSelected(new Set(TABS.map(t => t.key)))} style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Select All</button>
                <button onClick={() => setExportSelected(new Set())} style={{ padding: "5px 12px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Clear All</button>
              </div>
              <span style={{ fontSize: 12, color: "#9CA3AF" }}>{exportSelected.size} of {TABS.length} selected</span>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setExportPickerOpen(false)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={() => { setExportPickerOpen(false); exportAllToHTML(); }} disabled={exportSelected.size === 0}
                style={{ flex: 2, padding: "11px 0", borderRadius: 10, border: "none", background: exportSelected.size === 0 ? "#9CA3AF" : "linear-gradient(135deg,#001A3D,#003080)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: exportSelected.size === 0 ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: exportSelected.size === 0 ? "none" : "0 4px 14px rgba(0,26,61,0.3)" }}>
                🌐 Export {exportSelected.size} Report{exportSelected.size !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,15,40,0.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "30px 26px", maxWidth: 400, width: "100%", boxShadow: "0 24px 60px rgba(0,0,0,0.22)", textAlign: "center" }}>
            <div style={{ width: 54, height: 54, borderRadius: 16, background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 26 }}>🗑️</div>
            <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 800, color: "#111827" }}>Delete Record?</h3>
            <p style={{ margin: "0 0 6px", fontSize: 13, color: "#6B7280" }}>
              This will permanently remove this {tab.label.toLowerCase()} record.
            </p>
            <p style={{ margin: "0 0 22px", fontSize: 12, color: "#9CA3AF", fontStyle: "italic" }}>
              {deleteTarget.title || deleteTarget.name || deleteTarget.supplierName || deleteTarget.description || deleteTarget.poNumber || `ID: ${deleteTarget.id}`}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              <button onClick={confirmDelete} style={{ flex: 1, padding: "11px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 4px 12px rgba(239,68,68,0.3)" }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Style constants ──────────────────────────────────────────────────────────
const thSt  = { padding: "12px 14px", textAlign: "left", color: "rgba(255,255,255,0.9)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", whiteSpace: "nowrap", borderRight: "1px solid rgba(255,255,255,0.07)", userSelect: "none" };
const tdSt  = { padding: "11px 14px", borderRight: "1px solid #F3F4F6", verticalAlign: "middle" };
const pgSt  = { padding: "6px 10px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", minWidth: 32, lineHeight: 1, transition: "all 0.15s" };
const labelSt = { display: "block", fontSize: 10, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 };
const inputSt = { width: "100%", padding: "9px 12px", fontSize: 13, fontFamily: "inherit", background: "#fff", color: "#111827", border: "1.5px solid #E5E7EB", borderRadius: 9, outline: "none", boxSizing: "border-box" };
